import { inject, Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { ApiError, LoadGeneralData, LoadGeneralDataSuccess, LoadPage } from './actions';
import { firstValueFrom, map, switchMap, tap, withLatestFrom } from 'rxjs';
import { CookieService } from 'ngx-cookie-service';
import { select, Store } from '@ngrx/store';
import { D4CAppState } from './models';
import { CmsService } from '../services/cms.service';
import { Router } from '@angular/router';
import { AppLanguage } from '../models';
import { SetLanguage } from '@ca/ca-ng-core';
import { selectActivePageAndRole } from './selectors';

@Injectable()
export class GeneralEffects {
  private actions$ = inject(Actions);
  private cookies = inject(CookieService);
  private store: Store<D4CAppState> = inject(Store<D4CAppState>);
  private cms = inject(CmsService);
  private router = inject(Router);

  onSetLanguage$ = createEffect(
    () =>
      this.actions$.pipe(
        ofType(SetLanguage),
        withLatestFrom(
          this.store.pipe(
            select(selectActivePageAndRole),
          ),
        ),
        tap(([{ language, init }, { activePage, role }]) => {
          // set cookie
          if (!init) {
            this.cookies.set('user_language', language, 7, '/');
            const translatedBreadcrumbs = activePage?.breadcrumbs
              ?.map((b) => b.segments[language as AppLanguage])
              .filter((b) => b !== undefined);
            const loadPageData = {
              language,
              path: translatedBreadcrumbs ?? [],
            };
            this.store.dispatch(LoadPage(loadPageData));
            this.store.dispatch(LoadGeneralData({ language: language as AppLanguage }));
            this.router.navigate(
              translatedBreadcrumbs ? [`/${language}`, ...translatedBreadcrumbs] : [`/${language}`],
              { onSameUrlNavigation: 'reload' },
            );
          } else {
            this.store.dispatch(LoadGeneralData({ language: language as AppLanguage }));
          }
        }),
      ),
    { dispatch: false },
  );

  onLoadGeneralData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(LoadGeneralData),
      switchMap(({ language }) =>
        firstValueFrom(this.cms.getGeneral(language))
          .then((data) =>
            LoadGeneralDataSuccess({ data, language }),
          )
          .catch((error) => ApiError({ error })),
      ),
    ),
  );
}
